import _ from 'lodash';
import {
	TABLE_COLUMNS,
	SORT_ORDER,
	USER_ROLE_ADMIN,
	USER_ROLE_BASIC,
	USER_PAGE_SETTINGS_KEY,
	USER_TABLE_ALL,
	USER_ROLES,
	USER_TABLE_REDUCE_USERS
} from '../config';

export const PERMISSION_ROLE = [];
Object.keys(USER_ROLES).forEach(function (key) {
	PERMISSION_ROLE.push({ key, value: USER_ROLES[key].label, trackingName: USER_ROLES[key].trackingName });
});

const SMALL_BREAKPOINT_NAME_WIDTH = 150;
const SMALL_BREAKPOINT_EMAIL_WIDTH = 180;

/**
 * Check if the user already exists
 * @param {array} users all users in current company
 * @param {string} email user's email
 * @param {string} role The new role
 * @returns bool
 *
 * @memberof Users
 */
export const doesUserExist = (users, email, role) => {
	// If role is passed, check a user with the passed email and a role.
	if (role) {
		return users.some(user => (user.email.toLocaleLowerCase() === email.toLocaleLowerCase()
			&& (user.role.toLocaleLowerCase() === role.toLocaleLowerCase())));
	}

	return users.some(user => user.email.toLocaleLowerCase() === email.toLocaleLowerCase());
};

/**
 * Check if user is already in the invite list
 * @param {array} users users already added to invite popup
 * @param {string} email user's email
 * @returns bool
 *
 * @memberof Users
 */

export const isDuplicateEntry = (users, email, id) => {
	return users.some(user => user.email.toLocaleLowerCase() === email.toLocaleLowerCase() && user.id !== id);
};

/**
 * Return admins and members only
 * @param {array} users users in company
 * @returns array
 *
 * @memberof Users
 */

export const filterNonGuestUsers = (users) => {
	return users ? users.filter(user => [USER_ROLE_ADMIN, USER_ROLE_BASIC].includes(user.role)) : [];
};

// Maps user data from Middleware to data used by UI
export const encodeUser = (user) => {
	const { name, email, role, userId, externalIds, profilePhotoUri, inviteCounter } = user;
	return {
		name,
		email,
		trackingName: PERMISSION_ROLE.filter(item => item.key == role)[0].trackingName,
		role: PERMISSION_ROLE.filter(item => item.key == role)[0].value,
		userId,
		externalIds,
		profilePhotoUri,
		inviteCounter
	};
};

// Maps user data from UI to data used by Middleware
export const decodeUser = (user) => {
	const { email, role, userId, profilePhotoUri } = user;
	return {
		email,
		userId,
		trackingName: PERMISSION_ROLE.filter(item => item.value == role)[0].trackingName,
		role: PERMISSION_ROLE.filter(item => item.value == role)[0].key,
		profilePhotoUri
	};
};


/**
 * Sort users by key/order
*/
export const sortUsers = (users, sortKey, sortOrder) => {

	let result = users
		.sort((a, b) => {
			if (!sortKey || sortKey === TABLE_COLUMNS.FULL_NAME) {
				// sort by name ascending or decending first, then by email ascending
				return !sortOrder || sortOrder === SORT_ORDER.ASC
					?
					a.name.localeCompare(b.name) || a.email.localeCompare(b.email)
					:
					b.name.localeCompare(a.name) || a.email.localeCompare(b.email);
				
			} else if (sortKey == TABLE_COLUMNS.MLS_ID) {
				const firstAKey = _.get(a, 'externalIds[0]') || '';
				const firstBKey = _.get(b, 'externalIds[0]') || '';

				return sortOrder === SORT_ORDER.ASC
					?
					firstAKey.localeCompare(firstBKey)
					:
					firstBKey.localeCompare(firstAKey);
					
			} else if (sortKey == TABLE_COLUMNS.EMAIL) {

				return sortOrder === SORT_ORDER.ASC
					?
					a.email.localeCompare(b.email)
					:
					b.email.localeCompare(a.email);

			} else if (sortKey == TABLE_COLUMNS.ROLE) {
				// sort by role ascending or decending first, then by name and email ascending
				return sortOrder === SORT_ORDER.ASC
					?
					a.role.localeCompare(b.role) || a.name.localeCompare(b.name) || a.email.localeCompare(b.email)
					:
					b.role.localeCompare(a.role) || a.name.localeCompare(b.name) || a.email.localeCompare(b.email);

			}
		});

	if (!sortKey || sortKey == TABLE_COLUMNS.FULL_NAME || sortKey == TABLE_COLUMNS.MLS_ID) {
		// If Name or MLS ID is empty, it should be at the very bottom, but sorted by Email.
		result = result.sort(function (a, b) {
			const aNameEmpty = a.name.length === 0 ? 1 : 0;
			const bNameEmpty = b.name.length === 0 ? 1 : 0;
			if (aNameEmpty || bNameEmpty) {
				return aNameEmpty - bNameEmpty;
			}
		});
	}
		
	return result;
};

export const getSavedTableSettings = (settings, tableId, property) => {
	return _.get(settings, `cap[${USER_PAGE_SETTINGS_KEY}][${tableId}][${property}]`);
};

export const getTableStyleProps = (tableId, theme) => {
	
	return {
		/* no users message */
		noUsersFound: {
			width: '100%',
			textAlign: 'center'
		},

		/* table */
		table: {
			tableLayout: 'fixed',
			...(tableId === USER_TABLE_ALL && { marginBottom: 65}),
			...(tableId === USER_TABLE_REDUCE_USERS && { width: 'calc(100% - 15px)'})
		},
		tableHeadRow: {
			backgroundColor: '#F4F4F4'
		},
		tableBodyRow: {
			'&.transtionsEnabled': {
				transitionDuration: '1s !important',
			},
			'&:nth-of-type(even)': {
				backgroundColor: '#F4F4F4',
			},
			'&:hover': {
				backgroundColor: '#e8e8e8 !important'
			}
		},
		tableHasSortArrow: {
			position: 'relative',
			paddingLeft: 52
		},
		tableCellHeader: {
			fontSize: 14
		},
		tableNameCellHeader: {
			...(tableId === USER_TABLE_REDUCE_USERS && {
				width: 230,
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_NAME_WIDTH
				}
			}),
			'&.rowHasMls': {
			}
		},
		tableRoleCellHeader: {
			...(tableId === USER_TABLE_ALL && { width: 120, overflow: 'visible', paddingLeft: 28})
		},
		tableEmailCellHeader: {
			...(tableId === USER_TABLE_REDUCE_USERS && {
				width: 270,
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_EMAIL_WIDTH
				}				
			}),
			'&.rowHasMls': {
			}
		},
		tableMLSCellHeader: {
		},
		tableSelectActionCellHeader: {
			paddingLeft: 0,
			width: 205
		},
		tableCellBody: {
			fontSize: 14
		},
		tableActionsCell: {
			width: 40
		},
		tableCellReduceAction: {
			paddingRight: '5px !important'
		},
		noBorderedCell: {
		},
		verticalTopCell: {
			verticalAlign: 'top',
			paddingTop: 0
		},
		tableNameCellBody: {
			width: 230, 
			...(tableId === USER_TABLE_REDUCE_USERS && {
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_NAME_WIDTH
				}				
			})			
		},
		tableEmailCellBody: {
			width: 270, 
			...(tableId === USER_TABLE_REDUCE_USERS && {
				[theme.breakpoints.down('sm')]: {
					width: SMALL_BREAKPOINT_EMAIL_WIDTH
				}				
			})			
		},
		tableRoleCellBody: {
		},
		tableSelectActionCellBody: {
			width: 205,
		},

		/* table head */
		tableHead: {
			paddingBottom: 15
		},
		columnTableTitle: {
			width: 'auto'
		},
		tableTitle: {
			fontSize: '15px',
			lineHeight: '20px',
			fontWeight: 600
		},
		columnTableInfo: {
			width: 'auto',
			alignItems: 'center',
			marginLeft: 10
		},
		sortArrow: {
			position: 'absolute',
			top: '50%',
			transform: 'translateY(-50%)',
			left: 18,
			opacity: 0.3,
			'&.leftAligned': {
				left: -7
			},
			'&.active': {
				opacity: 1
			},
			'&.desc': {
				transform: 'translateY(-50%) rotate(-180deg)'
			}
		},

		/* role select */
		roleSelect: {
			minWidth: 115,
			'& [class*=MuiSelect-selectMenu]': {
				fontSize: 14
			}
		},
		roleSelectItems: {
			'& li': {
				fontSize: 14
			}		
		},

		/* MLS field */ 
		emptyExternalMlsId: {
			color: theme.palette.colors.colorWithAlpha(theme.palette.colors.zeta, 0.5),
			paddingTop: 8,
			minHeight: 38,
			font: '13px/19px Open Sans, sans-serif',
			'&:not(:first-child)': {
				marginTop: 10
			}
		},
		updatingMlsIdNode: {
			maxWidth: 370,
			paddingTop: 8,
			minHeight: 38,
			font: '13px/19px Open Sans, sans-serif',
			'&:not(:first-child)': {
				marginTop: 10
			}
		},
		nonEditableMlsIdValueNode: {
			maxWidth: 370,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			paddingLeft: 6,
			paddingTop: 8,
			minHeight: 38,
			font: '13px/19px Open Sans, sans-serif',
			'&:not(:first-child)': {
				marginTop: 10
			}
		},
		tableMLSCellHeaderLabel: {
			width: 50
		}
	};
};


// WEBPACK FOOTER //
// ./src/utils/users.js